import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DATA_REFRESH_EVENT } from '../../contexts/WebSocketContext';
import { 
  BookOpen, 
  Users, 
  Calendar, 
  FileCheck, 
  AlertTriangle,
  Clock,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Video,
  Plus,
  FileText,
  ClipboardCheck,
  Megaphone,
  Activity,
  BarChart3,
  Eye,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const TrainerDashboard = ({ onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState({ pending_corrections: 0, late_students: 0, today_sessions: 0 });
  const [analytics, setAnalytics] = useState({ avg_progress: 0, completion_rate: 0, success_rate: 0 });
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [coursesRes, studentsRes] = await Promise.all([
        // Use the content endpoint (carries is_published) and only surface published
        // courses so drafts assigned to the trainer don't leak onto the dashboard.
        api.get('/content/courses').catch(() => api.get('/courses')),
        api.get('/students')
      ]);
      const rawCourses = Array.isArray(coursesRes.data) ? coursesRes.data : [];
      setCourses(rawCourses.filter((c) => c.is_published));
      setStudents(studentsRes.data);
      
      // Fetch trainer-specific dashboard data from real API
      try {
        const dashboardRes = await api.get('/trainer/dashboard');
        if (dashboardRes.data) {
          setRecentActivity(dashboardRes.data.recent_activity || []);
          setAlerts(dashboardRes.data.alerts || { pending_corrections: 0, late_students: 0, today_sessions: 0 });
          setAnalytics(dashboardRes.data.analytics || { avg_progress: 0, completion_rate: 0, success_rate: 0 });
          setUpcomingSessions(dashboardRes.data.upcoming_sessions || []);
        }
      } catch (e) {
        console.warn('Dashboard API not available, using defaults');
        setRecentActivity([]);
        setAlerts({ pending_corrections: 0, late_students: 0, today_sessions: 0 });
        setAnalytics({ avg_progress: 0, completion_rate: 0, success_rate: 0 });
        setUpcomingSessions([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for real-time data refresh events
  useEffect(() => {
    const handleDataRefresh = (event) => {
      const { type } = event.detail;
      console.log('Trainer dashboard received data refresh:', type);
      
      // Refresh dashboard data when relevant events occur
      const refreshTypes = [
        'homework_created', 'homework_submitted', 'homework_graded',
        'live_session_created', 'evaluation_created', 'announcement_created'
      ];
      if (refreshTypes.includes(type)) {
        fetchData();
      }
    };

    window.addEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
    return () => window.removeEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
  }, [fetchData]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'submission': return <FileCheck className="w-4 h-4 text-blue-500" />;
      case 'completion': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'pass': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'fail': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4 text-slate-500" />;
    }
  };

  const statCards = [
    { 
      title: 'Mes Cours', 
      value: courses.length, 
      icon: BookOpen, 
      color: 'text-violet-600', 
      bg: 'bg-violet-50 dark:bg-violet-900/20',
      link: '/courses'
    },
    { 
      title: 'Mes Étudiants', 
      value: students.length, 
      icon: Users, 
      color: 'text-blue-600', 
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      link: '/students'
    },
    { 
      title: 'Sessions aujourd\'hui', 
      value: alerts.today_sessions, 
      icon: Video, 
      color: 'text-emerald-600', 
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      link: '/trainer/planning'
    },
    { 
      title: 'Devoirs à corriger', 
      value: alerts.pending_corrections, 
      icon: FileCheck, 
      color: 'text-amber-600', 
      bg: 'bg-amber-50 dark:bg-amber-900/20', 
      alert: alerts.pending_corrections > 5,
      link: '/trainer/grading'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Cockpit Formateur" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome & Quick Stats */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2">
            Bienvenue, {user?.first_name} !
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Votre tableau de bord pédagogique
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={index} 
                className={`stat-card cursor-pointer hover:shadow-lg transition-shadow ${stat.alert ? 'ring-2 ring-amber-400' : ''}`} 
                data-testid={`stat-card-${index}`}
                onClick={() => navigate(stat.link)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} strokeWidth={1.5} />
                    </div>
                    {stat.alert && (
                      <Badge className="bg-amber-100 text-amber-700">Urgent</Badge>
                    )}
                  </div>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{stat.title}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions Panel */}
        <Card className="mb-8">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-violet-600" />
              Actions Rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:border-violet-300"
                onClick={() => navigate('/trainer/create-assignment')}
                data-testid="quick-action-assignment"
              >
                <FileText className="w-6 h-6 text-violet-600" />
                <span className="text-sm font-medium">Créer un Devoir</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300"
                onClick={() => navigate('/trainer/create-evaluation')}
                data-testid="quick-action-evaluation"
              >
                <ClipboardCheck className="w-6 h-6 text-blue-600" />
                <span className="text-sm font-medium">Créer une Évaluation</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-300"
                onClick={() => navigate('/trainer/schedule-live')}
                data-testid="quick-action-live"
              >
                <Video className="w-6 h-6 text-emerald-600" />
                <span className="text-sm font-medium">Planifier un Live</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300"
                onClick={() => navigate('/trainer/send-announcement')}
                data-testid="quick-action-announcement"
              >
                <Megaphone className="w-6 h-6 text-amber-600" />
                <span className="text-sm font-medium">Envoyer une Annonce</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Pedagogical Alerts */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                Alertes Pédagogiques
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div 
                className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                onClick={() => navigate('/trainer/grading')}
              >
                <div className="flex items-center gap-3">
                  <FileCheck className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-slate-900 dark:text-white">Devoirs à corriger</span>
                </div>
                <Badge className="bg-amber-600 text-white">{alerts.pending_corrections}</Badge>
              </div>
              <div 
                className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                onClick={() => navigate('/students?filter=late')}
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-red-600" />
                  <span className="text-sm font-medium text-slate-900 dark:text-white">Étudiants en retard</span>
                </div>
                <Badge className="bg-red-600 text-white">{alerts.late_students}</Badge>
              </div>
              <div 
                className="flex items-center justify-between p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors"
                onClick={() => navigate('/trainer/planning')}
              >
                <div className="flex items-center gap-3">
                  <Video className="w-5 h-5 text-violet-600" />
                  <span className="text-sm font-medium text-slate-900 dark:text-white">Sessions aujourd'hui</span>
                </div>
                <Badge className="bg-violet-600 text-white">{alerts.today_sessions}</Badge>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => navigate('/trainer/grading')}>
                Voir les corrections
              </Button>
            </CardContent>
          </Card>

          {/* Class Analytics */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-violet-600" />
                Analytiques de Classe
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20">
                  <div className="relative w-20 h-20 mx-auto mb-3">
                    <svg className="w-20 h-20 transform -rotate-90">
                      <circle cx="40" cy="40" r="35" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                      <circle cx="40" cy="40" r="35" stroke="#8b5cf6" strokeWidth="6" fill="none"
                        strokeDasharray={`${analytics.avg_progress * 2.2} 220`} strokeLinecap="round" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-violet-600">
                      {analytics.avg_progress}%
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Progression Moyenne</p>
                  <p className="text-xs text-slate-500">des étudiants</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                  <div className="relative w-20 h-20 mx-auto mb-3">
                    <svg className="w-20 h-20 transform -rotate-90">
                      <circle cx="40" cy="40" r="35" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                      <circle cx="40" cy="40" r="35" stroke="#10b981" strokeWidth="6" fill="none"
                        strokeDasharray={`${analytics.completion_rate * 2.2} 220`} strokeLinecap="round" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-emerald-600">
                      {analytics.completion_rate}%
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Taux de Complétion</p>
                  <p className="text-xs text-slate-500">des modules</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
                  <div className="relative w-20 h-20 mx-auto mb-3">
                    <svg className="w-20 h-20 transform -rotate-90">
                      <circle cx="40" cy="40" r="35" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                      <circle cx="40" cy="40" r="35" stroke="#3b82f6" strokeWidth="6" fill="none"
                        strokeDasharray={`${analytics.success_rate * 2.2} 220`} strokeLinecap="round" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-blue-600">
                      {analytics.success_rate}%
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Taux de Réussite</p>
                  <p className="text-xs text-slate-500">aux évaluations</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Student Activity */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-violet-600" />
                Activité Récente des Étudiants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 dark:text-white">
                        <span className="font-medium">{activity.student}</span>
                        <span className="text-slate-500"> {activity.action} </span>
                        <span className="font-medium text-violet-600">{activity.item}</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{activity.time}</p>
                    </div>
                    {activity.type === 'submission' && (
                      <Button variant="ghost" size="sm" className="text-violet-600">
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" className="w-full">Voir toute l'activité</Button>
              </div>
            </CardContent>
          </Card>

          {/* My Courses */}
          <Card>
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-violet-600" />
                Mes Cours
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/courses')}>
                Voir tout <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {courses.slice(0, 4).map((course) => (
                  <div 
                    key={course.id} 
                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-600 hover:bg-violet-50/50 dark:hover:bg-violet-900/10 transition-all cursor-pointer"
                    onClick={() => navigate(`/trainer/courses/${course.id}`)}
                    data-testid={`course-card-${course.id}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                      {course.name?.substring(0, 2).toUpperCase() || 'C'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">{course.name}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" /> {course.enrolled_count || 12} étudiants
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> {course.module_count || 5} modules
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Sessions */}
        <Card className="mt-6">
          <CardHeader className="pb-4 flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-emerald-600" />
              Prochaines Sessions Live
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/trainer/planning')}>
              <Calendar className="w-4 h-4 mr-2" />
              Voir le planning
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingSessions.map((session) => (
                <div key={session.id} className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2 mb-2">
                    <Video className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">{session.date}</span>
                  </div>
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-1">{session.title}</h4>
                  <p className="text-xs text-slate-500">{session.course}</p>
                  <Button
                    size="sm"
                    className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => session.live_url ? window.open(session.live_url, '_blank') : toast.info('Aucun lien de réunion configuré')}
                  >
                    Démarrer la session
                  </Button>
                </div>
              ))}
              <div className="p-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center text-slate-500 cursor-pointer hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                onClick={() => navigate('/trainer/schedule-live')}>
                <Plus className="w-8 h-8 mb-2" />
                <span className="text-sm font-medium">Planifier une session</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TrainerDashboard;
